import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { CircularProgress, Grid, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { EApiRoutes } from 'api/api.utils';
import usePermissions from 'api/usePermissions';
import { useUserDetails } from 'api/useUserDetails';
import { AxiosError } from 'axios';
import { Portal } from 'common/contracts';
import { useFormik } from 'formik';
import { required } from 'utils/errorsTextHelper';
import { permissionsUtil } from 'utils/permissionsUtil';
import * as yup from 'yup';

import AcCard from 'components/AcCard/AcCard';
import AcInput from 'components/AcInput/AcInput';
import ActionButton from 'components/ActionButton/ActionButton';

import usePortalSettings from '../../../api/portal/usePortalSettings';
import { EToasterStatus, showToast } from '../../../utils/showToast';

import 'style/forms.scss';


const PortalSettings = () => {
  const { portalId } = useParams();
  const { updateBasicPortalSettings } = usePortalSettings();

  const { userDetails } = useUserDetails();
  const { isAdmin } = usePermissions();
  const queryClient = useQueryClient();

  const data: any = userDetails?.portals?.find(
    (portal: Portal) => portal.portalId === portalId
  );

  let portalSettingsSchema = yup.object().shape({
    portalName: yup.string().required(required('Name')),
    domain: yup
      .string()
      .required(required('Domain'))
      .matches(
        /^(?!https?:\/\/)(?!.*\/$)[a-zA-Z0-9.-]+$/,
        "Please enter a valid domain name, without http://' or 'https://' prefixes or suffixes"
      )
  });

  const initialValues = useMemo(
    () => ({
      portalName: data?.portalName || '',
      domain: data?.portalDomain || '',
      portalId: data?.portalId || ''
    }),
    [data]
  );

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitForm,
    isSubmitting,
    dirty,
    isValid
  } = useFormik({
    initialValues,
    validationSchema: portalSettingsSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await updateBasicPortalSettings.mutateAsync({ 
          portalName: values.portalName,
          portalDomain: values.domain
        });

        // Update React Query cache to keep it in sync with API
        if (userDetails) {
          const updatedPortals = userDetails.portals?.map((portal: Portal) =>
            portal.portalId === portalId
              ? { ...portal, portalName: values.portalName, portalDomain: values.domain }
              : portal
          );

          queryClient.setQueryData([EApiRoutes.USERS], {
            ...userDetails,
            portals: updatedPortals
          });
        }

        showToast({
          message: 'Portal settings were successfully updated',
          status: EToasterStatus.SUCCESS
        });
      } catch (error: unknown) {
        // Type guard to ensure the error is an AxiosError
        if (error instanceof AxiosError) {
          showToast({
            message: 'Failed to update portal settings. Please try again.',
            status: EToasterStatus.ERROR
          });
        } else {
          showToast({
            message: 'An unknown error occurred. Please try again.',
            status: EToasterStatus.ERROR
          });
        }
      }
    }
  });

  if (!data) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 340px)',
          width: '100%'
        }}
      >
        <CircularProgress title="Loading..." />
      </div>
    );
  }

  return (
    <Grid container className="formContent">
      <Grid item xs={5} className="formContent-minWIdth">
        <Stack>
          <AcCard stackContainer={false} title="General">
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={6}>
                <AcInput
                  header="Portal Name"
                  name="portalName"
                  value={values.portalName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.portalName && Boolean(errors.portalName)}
                  helperText={
                    touched.portalName ? errors.portalName?.toString() : ''
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <AcInput
                  header="Portal ID"
                  disabled
                  name="portalId"
                  value={data?.portalId}
                />
              </Grid>
              <Grid item xs={6}>
                <AcInput
                  header="Domain"
                  name="domain"
                  tooltip="Enter a domain name (e.g., example.com ) without 'http://' or 'https://'"
                  value={values.domain}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.domain && Boolean(errors.domain)}
                  helperText={touched.domain ? errors.domain?.toString() : ''}
                  enableNewTabOpen
                  disabled={!isAdmin}
                />
              </Grid>
            </Grid>
          </AcCard>
        </Stack>
        <Grid item xs={6} mt={3}>
          <ActionButton
            variant="outlined"
            text="Update"
            onClick={submitForm}
            disabled={!isValid || !dirty || isSubmitting}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PortalSettings;
